import React, { useEffect, useState } from 'react';
import TopBar from './components/TopBar';
import Sidebar from './components/Sidebar';
import CorpTable from './components/CorpTable';
import CorpFormScreen from './components/CorpFormScreen';
import ClientTable from './components/ClientTable';
import ClientForm from './components/ClientForm';
import VendorTable from './components/VendorTable';
import VendorForm from './components/VendorForm';
import EmployeeTable from './components/EmployeeTable';
import EmployeeForm from './components/EmployeeForm';
import InvoiceTable from './components/InvoiceTable';
import InvoiceForm from './components/InvoiceForm';
import OrderTable from './components/OrderTable';
import OrderFormScreen from './components/OrderFormScreen';
import TimesheetTable from './components/TimesheetTable';
import TimesheetForm from './components/TimesheetForm';
import TimesheetItemTable from './components/TimesheetItemTable';
import ExpenseTable from './components/ExpenseTable';
import DocumentsTable from './components/DocumentsTable';
import AdminForm from './components/AdminForm';
import Settings from './components/Settings';
import { fetchWithCorpId } from './utils/api';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import Login from './components/Login';
import { useAuth, AuthProvider } from './utils/auth';
import QuickTime from './components/QuickTime';
import PasswordReset from './components/PasswordReset';
import Dashboard from './components/Dashboard';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import GlobalProgress from './components/ui/GlobalProgress';
import NotFound from './components/ui/NotFound';

// Import CSS files
import './styles/base.css';
import './styles/layout.css';
import './styles/buttons.css';
import './styles/forms.css';
import './styles/components.css';

const NoAccess = () => (
  <div style={{ padding: '64px 24px', textAlign: 'center' }}>
    <h2 style={{
      fontFamily: 'var(--font-sans)',
      fontSize: '18px',
      fontWeight: 600,
      color: 'var(--ink)',
      marginBottom: '8px',
    }}>
      No access
    </h2>
    <p style={{
      fontFamily: 'var(--font-sans)',
      fontSize: '14px',
      color: 'var(--muted)',
      marginBottom: '24px',
    }}>
      Your account does not have access to this page.
    </p>
    <Link to="/on/dashboard" className="btn-primary" style={{ textDecoration: 'none' }}>
      Go to Dashboard
    </Link>
  </div>
);

// Browser tab titles per screen. First matching prefix wins.
const PAGE_TITLES = [
  ['/on/dashboard', 'Dashboard'],
  ['/on/corps', 'Corporations'],
  ['/on/clients', 'Clients'],
  ['/on/vendors', 'Vendors'],
  ['/on/employees', 'Employees'],
  ['/on/invoices', 'Invoices'],
  ['/on/orders', 'Orders'],
  ['/on/timesheet-items', 'Timesheet Details'],
  ['/on/timesheets', 'Timesheets'],
  ['/on/expenses', 'Expenses'],
  ['/on/documents', 'Documents'],
  // '/on/admin' itself redirects to Settings; the prefix still titles the
  // standalone /on/admin/new and /on/admin/edit/:id screens.
  ['/on/admin', 'Corporation'],
  ['/on/settings', 'Settings'],
  ['/on/security', 'Security'],
  ['/on/login', 'Sign in'],
  ['/on/password-reset', 'Password reset'],
  ['/on/quick-time', 'Timesheet entry'],
];

const usePageChrome = () => {
  const location = useLocation();
  useEffect(() => {
    const match = PAGE_TITLES.find(([prefix]) => location.pathname.startsWith(prefix));
    document.title = match ? `${match[1]} · One Nexus` : 'One Nexus';
    // The app scrolls inside <main>, not the window; reset it on navigation.
    const main = document.querySelector('main');
    if (main) main.scrollTo(0, 0);
  }, [location.pathname]);
};

// Protected Route component. `permission` is a feature key from ROLE_PERMISSIONS
// (utils/auth.js); routes without one only require a signed-in user.
const ProtectedRoute = ({ children, selectedCorp, permission }) => {
  const { user, isInitialized, hasPermission } = useAuth();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === '1'
  );
  const toggleSidebar = () => setSidebarCollapsed(c => {
    const next = !c;
    localStorage.setItem('sidebarCollapsed', next ? '1' : '0');
    return next;
  });
  if (!isInitialized) return null;
  if (!user) {
    // Remember where the user was headed so login can return them there.
    return <Navigate to="/on/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (permission && !hasPermission(permission)) {
    children = <NoAccess />;
  }
  return (
    <div className="layout">
      <TopBar selectedCorp={selectedCorp} />
      <div className="content">
        <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
        <main>
          {children}
        </main>
      </div>
    </div>
  );
};

// Main App component
const AppContent = () => {
  const [selectedCorp, setSelectedCorp] = useState(null);
  const navigate = useNavigate();
  usePageChrome();

  // Load selected corporation on mount
  useEffect(() => {
    const loadSelectedCorp = async () => {
      const storedCorpData = localStorage.getItem('selectedCorpData');
      if (storedCorpData) {
        setSelectedCorp(JSON.parse(storedCorpData));
        localStorage.removeItem('selectedCorpData');
        return;
      }
      const corpId = localStorage.getItem('selectedCorpId');
      if (corpId) {
        try {
          const corp = await fetchWithCorpId(`/corps/${corpId}`);
          setSelectedCorp(corp);
        } catch (error) {
          localStorage.removeItem('selectedCorpId');
        }
      }
    };
    loadSelectedCorp();
  }, []);

  const handleCorpSwitch = (corp) => {
    setSelectedCorp(corp);
    localStorage.setItem('selectedCorpId', corp.id);
  };

  // Edit handlers for each entity type
  const handleCorpEdit = (corp) => {
    navigate(`/on/corps/edit/${corp.id}`);
  };
  const handleClientEdit = (client) => {
    navigate(`/on/clients/edit/${client.id}`);
  };
  const handleClientEditComplete = () => {
    navigate('/on/clients');
  };
  const handleVendorEdit = (vendor) => {
    navigate(`/on/vendors/edit/${vendor.id}`);
  };
  const handleVendorEditComplete = () => {
    navigate('/on/vendors');
  };
  const handleEmployeeEdit = (employee) => {
    navigate(`/on/employees/edit/${employee.id}`);
  };
  const handleInvoiceEdit = (invoice) => {
    navigate(`/on/invoices/edit/${invoice.id}`);
  };
  const handleOrderEdit = (order) => {
    navigate(`/on/orders/edit/${order.id}`);
  };
  const handleTimesheetEdit = (timesheet) => {
    navigate(`/on/timesheets/edit/${timesheet.id}`);
  };
  // Success handlers for each entity type
  const handleCorpEditComplete = () => {
    navigate('/on/corps');
    refreshSelectedCorp();
  };
  const handleEmployeeEditComplete = () => {
    navigate('/on/employees');
  };
  const handleInvoiceEditComplete = () => {
    navigate('/on/invoices');
  };
  const handleOrderEditComplete = () => {
    navigate('/on/orders');
  };
  const handleTimesheetEditComplete = () => {
    navigate('/on/timesheets');
  };
  const handleAdminEditComplete = () => {
    navigate('/on/settings?tab=corporation');
  };

  // Helper function to refresh selected corporation
  const refreshSelectedCorp = async () => {
    const corpId = localStorage.getItem('selectedCorpId');
    if (corpId) {
      try {
        const corp = await fetchWithCorpId(`/corps/${corpId}`);
        setSelectedCorp(corp);
      } catch (error) {
        localStorage.removeItem('selectedCorpId');
      }
    }
  };

  const handleTimesheetView = (timesheet) => {
    navigate(`/on/timesheet-items?timesheetId=${timesheet.id}`);
  };

  return (
    <Routes>
      <Route path="/on" element={<Navigate to="/on/dashboard" replace />} />
      <Route path="/on" >
        <Route path="login" element={<Login />} />
        <Route path="password-reset" element={<PasswordReset />} />
        <Route path="dashboard" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="landing" element={<Navigate to="/on/dashboard" replace />} />
        <Route path="corps" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="corps">
            <CorpTable
              onEdit={handleCorpEdit}
              selectedCorp={selectedCorp}
              onCorpSwitch={handleCorpSwitch}
            />
          </ProtectedRoute>
        } />
        <Route path="corps/new" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="corps">
            <CorpFormScreen
              mode="create"
              onSuccess={handleCorpEditComplete}
              onCancel={() => navigate('/on/corps')}
            />
          </ProtectedRoute>
        } />
        <Route path="corps/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="corps">
            <CorpFormScreen
              mode="edit"
              onSuccess={handleCorpEditComplete}
              onCancel={() => navigate('/on/corps')}
            />
          </ProtectedRoute>
        } />
        <Route path="clients" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="clients">
            <ClientTable onEdit={handleClientEdit} />
          </ProtectedRoute>
        } />
        <Route path="clients/new" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="clients">
            <ClientForm
              mode="create"
              onSuccess={handleClientEditComplete}
              onCancel={() => navigate('/on/clients')}
            />
          </ProtectedRoute>
        } />
        <Route path="clients/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="clients">
            <ClientForm
              mode="edit"
              onSuccess={handleClientEditComplete}
              onCancel={() => navigate('/on/clients')}
            />
          </ProtectedRoute>
        } />
        <Route path="vendors" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="vendors">
            <VendorTable onEdit={handleVendorEdit} />
          </ProtectedRoute>
        } />
        <Route path="vendors/new" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="vendors">
            <VendorForm
              mode="create"
              onSuccess={handleVendorEditComplete}
              onCancel={() => navigate('/on/vendors')}
            />
          </ProtectedRoute>
        } />
        <Route path="vendors/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="vendors">
            <VendorForm
              mode="edit"
              onSuccess={handleVendorEditComplete}
              onCancel={() => navigate('/on/vendors')}
            />
          </ProtectedRoute>
        } />
        <Route path="employees" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="employees">
            <EmployeeTable onEdit={handleEmployeeEdit} />
          </ProtectedRoute>
        } />
        <Route path="employees/new" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="employees">
            <EmployeeForm
              mode="create"
              onSuccess={handleEmployeeEditComplete}
              onCancel={() => navigate('/on/employees')}
            />
          </ProtectedRoute>
        } />
        <Route path="employees/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="employees">
            <EmployeeForm
              mode="edit"
              onSuccess={handleEmployeeEditComplete}
              onCancel={() => navigate('/on/employees')}
            />
          </ProtectedRoute>
        } />
        <Route path="invoices" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="invoices">
            <InvoiceTable onEdit={handleInvoiceEdit} />
          </ProtectedRoute>
        } />
        <Route path="invoices/new" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="invoices">
            <InvoiceForm
              mode="create"
              onSuccess={handleInvoiceEditComplete}
              onCancel={() => navigate('/on/invoices')}
            />
          </ProtectedRoute>
        } />
        <Route path="invoices/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="invoices">
            <InvoiceForm
              mode="edit"
              onSuccess={handleInvoiceEditComplete}
              onCancel={() => navigate('/on/invoices')}
            />
          </ProtectedRoute>
        } />
        <Route path="orders" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="order">
            <OrderTable onEdit={handleOrderEdit} />
          </ProtectedRoute>
        } />
        <Route path="orders/new" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="order">
            <OrderFormScreen
              mode="create"
              onSuccess={handleOrderEditComplete}
              onCancel={() => navigate('/on/orders')}
            />
          </ProtectedRoute>
        } />
        <Route path="orders/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="order">
            <OrderFormScreen
              mode="edit"
              onSuccess={handleOrderEditComplete}
              onCancel={() => navigate('/on/orders')}
            />
          </ProtectedRoute>
        } />
        <Route path="timesheets" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="timesheets">
            <TimesheetTable onEdit={handleTimesheetEdit} onView={handleTimesheetView} />
          </ProtectedRoute>
        } />
        <Route path="timesheets/new" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="timesheets">
            <TimesheetForm
              mode="create"
              onSuccess={handleTimesheetEditComplete}
              onCancel={() => navigate('/on/timesheets')}
            />
          </ProtectedRoute>
        } />
        <Route path="timesheets/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="timesheets">
            <TimesheetForm
              mode="edit"
              onSuccess={handleTimesheetEditComplete}
              onCancel={() => navigate('/on/timesheets')}
            />
          </ProtectedRoute>
        } />
        <Route path="timesheet-items" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="timesheetItems">
            <TimesheetItemTable />
          </ProtectedRoute>
        } />
        <Route path="expenses" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="expenses">
            <ExpenseTable />
          </ProtectedRoute>
        } />
        <Route path="documents" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="documents">
            <DocumentsTable />
          </ProtectedRoute>
        } />
        <Route path="settings" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <Settings />
          </ProtectedRoute>
        } />
        {/* Old bookmark: password change now lives in Settings > Security. */}
        <Route path="security" element={<Navigate to="/on/settings?tab=security" replace />} />
        <Route path="quick-time" element={<QuickTime />} />
        {/* Old bookmark: corporation settings now live in Settings > Corporation (#118). */}
        <Route path="admin" element={<Navigate to="/on/settings?tab=corporation" replace />} />
        <Route path="admin/new" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="admin">
            <AdminForm
              mode="create"
              onSuccess={handleAdminEditComplete}
              onCancel={() => navigate('/on/settings?tab=corporation')}
            />
          </ProtectedRoute>
        } />
        <Route path="admin/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp} permission="admin">
            <AdminForm
              mode="edit"
              onSuccess={handleAdminEditComplete}
              onCancel={() => navigate('/on/settings?tab=corporation')}
            />
          </ProtectedRoute>
        } />
      </Route>
      <Route path="/" element={<Navigate to="/on/dashboard" replace />} />
      <Route path="*" element={
        <ProtectedRoute selectedCorp={selectedCorp}>
          <NotFound />
        </ProtectedRoute>
      } />
    </Routes>
  );
};

// Wrap the app with providers: auth, toasts, confirm dialogs.
const App = () => (
  <AuthProvider>
    <ToastProvider>
      <ConfirmProvider>
        <Router>
          <GlobalProgress />
          <AppContent />
        </Router>
      </ConfirmProvider>
    </ToastProvider>
  </AuthProvider>
);

export default App;
